import React, { useEffect } from "react";
import { MenuItem } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { excelFloor, toDecimal } from "../../helpers/PriceShopEngine";

interface SelectCustomizationComponentProps {
  componentType: string;
  components: PCComponent[] | undefined;
  selectedComponent: SelectedComponent | null;
  margin: number;
  vatRate: number;
  currency: string;
  onChange: (event: SelectChangeEvent, componentType: string) => void;
  showCosts: Boolean;
}

const SelectCustomizationComponent: React.FC<
  SelectCustomizationComponentProps
> = ({
  componentType,
  components,
  selectedComponent,
  margin,
  vatRate,
  currency,
  onChange,
  showCosts,
}) => {
  const [defaultComponent, setDefaultComponent] =
    React.useState<PCComponent | null>(null);
  const [component, setComponent] = React.useState<PCComponent | null>(null);

  // NOTE: All hooks MUST be declared before any conditional early return,
  // otherwise React's hook-ordering invariants are violated and sibling
  // instances of this component may end up sharing state slots — which
  // manifested as "selecting a monitor also marks the keyboard as selected".
  // See: https://react.dev/reference/rules/rules-of-hooks
  useEffect(() => {
    if (!components || components.length === 0) {
      setDefaultComponent(null);
      setComponent(null);
      return;
    }
    const defaultComp = components.find((c) => c.baseComponent) || null;
    setDefaultComponent(defaultComp);
    const findById = (id: string | null) =>
      id ? components.find((c) => c.id === id) || null : null;
    setComponent(findById(selectedComponent?.id ?? null));
  }, [components, selectedComponent]);

  if (!components || components.length === 0) return null;
  if (!components.some((c) => c.optional)) {
    return null;
  }

  const isBaseComponent = (componentId: string | null): boolean => {
    if (null === componentId || componentId === "empty_option") return true;
    return defaultComponent?.id === componentId;
  };

  // API prices are differentials from the base component (Kotlin's
  // adjustComponentPricesUsingBaseComponentAsReference). price.value is
  // already the total differential PVP with VAT — includes quantity and
  // base subtraction. No quantity multiplication needed.
  const getCustomizationCost = (component: PCComponent | null): number => {
    if (!component) return 0;
    const sel = component.price.value || 0;
    const base = defaultComponent?.price?.value || 0;
    return toDecimal(sel - base);
  };

  // PVP in the dropdown uses price.pvp_with_vat directly (total
  // differential with VAT and margin, matches aggregate header).
  // Keep getCustomizationCost as the Custo source; PVP derives from cost
  // via margin+vat for display, or falls back to raw pvp_with_vat diff.
  const getCustomizationPVP = (component: PCComponent | null): number => {
    if (!component) return 0;
    const sel = component.price?.pvp_with_vat ?? 0;
    const base = defaultComponent?.price?.pvp_with_vat ?? 0;
    return toDecimal(sel - base);
  };

  const getCurrentCustomizationCost = (): number => {
    return getCustomizationCost(component);
  };

  const getCurrentCustomizationPVP = (): number => {
    return getCustomizationPVP(component);
  };

  return (
    <li className="columns is-multiline component-list-item">
      <p className="column is-3 md:text-right text-gray-800 font-bold lg:text-xl pt-4">
        {components[0]?.type ?? ""}
      </p>
      {(components.length > 1 || components[0]?.optional) && (
        <Select
          className={`not-printable w-full text-ellipsis z-0.9 ${components.length > 1 || components[0]?.optional ? "" : "is-hidden"}`}
          displayEmpty={true}
          value={selectedComponent?.id ?? "empty_option"}
          slotProps={{
            root: { className: "text-xs column is-9 p-0" },
            input: { className: "text-xs" },
          }}
          onChange={(event) => onChange(event, componentType)}
        >
          {components
            // ponytail: sort by catalog PVP for UI ordering (not reseller cost)
            .sort((a, b) => (a?.price?.value || 0) - (b?.price?.value || 0))
            .map((option, index) => (
              <MenuItem
                key={index}
                value={option.id}
                style={{ display: "block" }}
              >
                <span className="text-gray-800 font-bold">
                  {option.quantity > 1 ? `${option.quantity} x ` : ""}
                  {option.description}
                </span>
                {!isBaseComponent(option?.id ?? null) ? (
                  <span className="float-right text-gray-400 text-xs">
                    {getCustomizationPVP(option).toLocaleString("pt-PT", {
                      style: "currency",
                      currency: currency,
                    })}
                  </span>
                ) : (
                  <span className="float-right text-gray-400 text-xs">
                    Base
                  </span>
                )}
              </MenuItem>
            ))}
        </Select>
      )}
      <p
        className={`column is-9 text-left text-gray-400 text pt-4 force-print ${components.length > 1 || components[0]?.optional ? "is-hidden" : ""}`}
      >
        {components[0]?.description ?? ""}
      </p>
      {!isBaseComponent(selectedComponent?.id ?? null) && (
        <>
          <p className="text column is-offset-3 is-3 font-bold text-red-400 cost">
            {(getCurrentCustomizationPVP() || 0) > 0 ? "+" : " "}
            {getCurrentCustomizationPVP().toLocaleString("pt-PT", {
              style: "currency",
              currency: currency,
            })}
          </p>
          {showCosts && (
            <p className="text column is-offset-1 is-5 font-bold text-gray-400 reseller-cost">
              Custo: {(getCurrentCustomizationCost() || 0) > 0 ? "+" : " "}
              {getCurrentCustomizationCost().toLocaleString("pt-PT", {
                style: "currency",
                currency: currency,
              })}
            </p>
          )}
        </>
      )}
    </li>
  );
};

export default SelectCustomizationComponent;
